<script setup lang="ts">
import { computed } from 'vue';
import { Analytics } from '../analytics';

const props = defineProps<{
  page: 'terms' | 'privacy' | 'rules' | 'help' | 'contact' | 'how_it_works';
}>();

const emit = defineEmits(['close']);

const titles: Record<string, string> = {
  terms: 'Términos y Condiciones',
  privacy: 'Política de Privacidad',
  rules: 'Reglas del Juego',
  help: 'Centro de Ayuda',
  contact: 'Contacto',
  how_it_works: 'Cómo Funciona',
};

const lastUpdated = '16 de Enero de 2026';

const content: Record<string, string> = {
  terms: `
    <article itemscope itemtype="https://schema.org/WebPage">
      <meta itemprop="dateModified" content="2026-01-16" />
      <div class="prose prose-invert max-w-none">
        <p class="text-gray-400 text-sm mb-8">Última actualización: ${lastUpdated}</p>
        
        <section id="introduccion">
          <h3>1. Introducción y Aceptación</h3>
          <p>Bienvenido a <strong>Rifas La Favorita</strong> (en adelante "La Plataforma"), operada por Rifas La Favorita C.A., con domicilio en Venezuela. Al acceder, registrarse o utilizar nuestros servicios de sorteos en línea, usted acepta de manera expresa, voluntaria e irrevocable todos los términos y condiciones aquí establecidos.</p>
          <p>Si no está de acuerdo con estos términos, le solicitamos abstenerse de utilizar nuestros servicios.</p>
        </section>

        <section id="elegibilidad">
          <h3>2. Elegibilidad y Requisitos de Participación</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li><strong>Edad Mínima:</strong> Debe ser mayor de 18 años cumplidos para participar en cualquier sorteo.</li>
            <li><strong>Residencia:</strong> Nuestros sorteos están abiertos a participantes de cualquier país, sin restricciones geográficas.</li>
            <li><strong>Identificación:</strong> Se requiere cédula de identidad venezolana o pasaporte válido para verificar su identidad al reclamar premios.</li>
            <li><strong>Cuenta Única:</strong> Cada participante puede tener una sola cuenta. Cuentas duplicadas serán canceladas.</li>
          </ul>
        </section>

        <section id="compras">
          <h3>3. Proceso de Compra y Pagos</h3>
          <div class="bg-white/5 rounded-xl p-4 my-4">
            <h4 class="text-gold-400 font-bold mb-2">Métodos de Pago Aceptados:</h4>
            <ul class="list-none space-y-2">
              <li>💳 <strong>Pago Móvil</strong> - Bancos venezolanos</li>
              <li>🏦 <strong>Transferencia Bancaria</strong> - Bolívares</li>
              <li>💵 <strong>Zelle</strong> - Dólares americanos</li>
              <li>🟡 <strong>Binance Pay</strong> - USDT/BTC</li>
              <li>💵 <strong>Efectivo</strong> - Coordinado con vendedores autorizados</li>
            </ul>
          </div>
          <p>Todos los pagos deben ser reportados vía WhatsApp con el comprobante correspondiente. La validación toma un máximo de <strong>24 horas hábiles</strong>.</p>
          <p class="text-red-400"><strong>Importante:</strong> Las compras son finales. No se realizan reembolsos una vez asignados los tickets, excepto en caso de cancelación del sorteo por causa de fuerza mayor.</p>
        </section>

        <section id="tickets">
          <h3>4. Asignación y Validez de Tickets</h3>
          <p>Los números de tickets son <strong>asignados aleatoriamente</strong> por nuestro sistema de forma criptográficamente segura. Cada ticket posee:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>Código QR único con firma digital</li>
            <li>Número de serie verificable en nuestra plataforma</li>
            <li>Vinculación con datos del participante</li>
          </ul>
          <p>Solo se consideran válidos los tickets cuyo pago haya sido <strong>verificado y aprobado</strong> por nuestro equipo administrativo.</p>
        </section>

        <section id="premios">
          <h3>5. Premios y Entrega</h3>
          <p>Los ganadores serán contactados por teléfono, correo electrónico y WhatsApp dentro de las <strong>24 horas</strong> siguientes al sorteo.</p>
          <ul class="list-disc pl-6 space-y-2">
            <li><strong>Premios en efectivo:</strong> Transferencia bancaria o pago móvil dentro de 48-72 horas.</li>
            <li><strong>Premios físicos:</strong> Coordinación de entrega en punto acordado o envío nacional.</li>
            <li><strong>Plazo de reclamo:</strong> 7 días continuos desde la fecha del sorteo.</li>
          </ul>
        </section>

        <section id="responsabilidades">
          <h3>6. Limitación de Responsabilidad</h3>
          <p>Rifas La Favorita no será responsable por:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>Datos incorrectos proporcionados por el participante</li>
            <li>Fallas en servicios de terceros (bancos, telefonía, internet)</li>
            <li>Caso fortuito o fuerza mayor que impida la realización del sorteo</li>
          </ul>
        </section>

        <section id="modificaciones">
          <h3>7. Modificaciones a los Términos</h3>
          <p>Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios serán notificados a través de nuestra plataforma y redes sociales con al menos <strong>48 horas de anticipación</strong>.</p>
        </section>

        <section id="jurisdiccion">
          <h3>8. Ley Aplicable y Jurisdicción</h3>
          <p>Estos términos se rigen por las leyes de la República Bolivariana de Venezuela. Cualquier controversia será sometida a los tribunales competentes del Área Metropolitana de Caracas.</p>
        </section>
      </div>
    </article>
  `,
  privacy: `
    <article itemscope itemtype="https://schema.org/WebPage">
      <meta itemprop="dateModified" content="2026-01-16" />
      <div class="prose prose-invert max-w-none">
        <p class="text-gray-400 text-sm mb-8">Última actualización: ${lastUpdated}</p>
        
        <section id="recoleccion">
          <h3>1. Información que Recolectamos</h3>
          <p>Para brindarte nuestros servicios, recolectamos la siguiente información:</p>
          <div class="grid md:grid-cols-2 gap-4 my-4">
            <div class="bg-white/5 rounded-xl p-4">
              <h4 class="text-gold-400 font-bold mb-2">Datos Personales</h4>
              <ul class="list-disc pl-4 text-sm space-y-1">
                <li>Nombre completo</li>
                <li>Cédula de identidad</li>
                <li>Número de teléfono</li>
                <li>Correo electrónico</li>
                <li>País de residencia</li>
              </ul>
            </div>
            <div class="bg-white/5 rounded-xl p-4">
              <h4 class="text-gold-400 font-bold mb-2">Datos de Transacción</h4>
              <ul class="list-disc pl-4 text-sm space-y-1">
                <li>Método de pago utilizado</li>
                <li>Referencia de pago</li>
                <li>Monto pagado</li>
                <li>Fecha y hora de compra</li>
                <li>Tickets adquiridos</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="uso">
          <h3>2. Uso de la Información</h3>
          <p>Tu información es utilizada exclusivamente para:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>Procesar y verificar tus compras de tickets</li>
            <li>Contactarte en caso de resultar ganador</li>
            <li>Enviarte notificaciones sobre tus participaciones</li>
            <li>Mejorar nuestros servicios y experiencia de usuario</li>
            <li>Cumplir con obligaciones legales</li>
          </ul>
        </section>

        <section id="compartir">
          <h3>3. Compartición de Datos</h3>
          <p class="text-green-400 font-bold">🔒 NO vendemos, alquilamos ni compartimos tus datos personales con terceros.</p>
          <p>Excepciones limitadas:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li><strong>Procesadores de pago:</strong> Solo la información necesaria para procesar transacciones</li>
            <li><strong>Requerimientos legales:</strong> Cuando la ley venezolana lo exija expresamente</li>
            <li><strong>Publicación de ganadores:</strong> Solo nombre y número de ticket (previa autorización)</li>
          </ul>
        </section>

        <section id="seguridad">
          <h3>4. Seguridad de Datos</h3>
          <p>Implementamos medidas de seguridad de nivel empresarial:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>Encriptación SSL/TLS en todas las transmisiones</li>
            <li>Almacenamiento seguro en servidores de Cloudflare</li>
            <li>Acceso restringido solo a personal autorizado</li>
            <li>Auditorías de seguridad periódicas</li>
            <li>Firmas criptográficas en tickets digitales</li>
          </ul>
        </section>

        <section id="cookies">
          <h3>5. Cookies y Tecnologías de Seguimiento</h3>
          <p>Utilizamos cookies esenciales para:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>Mantener tu sesión activa</li>
            <li>Recordar preferencias de idioma</li>
            <li>Analítica anónima (Google Analytics)</li>
          </ul>
          <p>Puedes desactivar las cookies en tu navegador, aunque esto puede afectar la funcionalidad del sitio.</p>
        </section>

        <section id="derechos">
          <h3>6. Tus Derechos (ARCO)</h3>
          <p>Tienes derecho a:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li><strong>Acceso:</strong> Solicitar copia de tus datos personales</li>
            <li><strong>Rectificación:</strong> Corregir datos inexactos</li>
            <li><strong>Cancelación:</strong> Solicitar eliminación de tus datos</li>
            <li><strong>Oposición:</strong> Oponerte al tratamiento de tus datos</li>
          </ul>
          <p>Para ejercer estos derechos, contáctanos a: <a href="mailto:privacidad@lafavoritarifas.com" class="text-gold-400 hover:underline">privacidad@lafavoritarifas.com</a></p>
        </section>

        <section id="menores">
          <h3>7. Protección de Menores</h3>
          <p>Nuestros servicios están dirigidos exclusivamente a personas mayores de 18 años. No recolectamos intencionalmente datos de menores de edad. Si descubrimos que hemos recibido información de un menor, la eliminaremos inmediatamente.</p>
        </section>

        <section id="contacto-privacidad">
          <h3>8. Contacto para Asuntos de Privacidad</h3>
          <div class="bg-gold-500/10 border border-gold-500/30 rounded-xl p-4">
            <p><strong>Oficial de Protección de Datos:</strong></p>
            <p>📧 Email: <a href="mailto:privacidad@lafavoritarifas.com" class="text-gold-400">privacidad@lafavoritarifas.com</a></p>
            <p>📱 WhatsApp: <a href="https://wa.me/584167452305" class="text-gold-400">+58 416 745 2305</a></p>
          </div>
        </section>
      </div>
    </article>
  `,
  rules: `
    <article itemscope itemtype="https://schema.org/WebPage">
      <meta itemprop="dateModified" content="2026-01-16" />
      <div class="prose prose-invert max-w-none">
        <div class="bg-red-900/20 border border-red-500/30 rounded-xl p-4 mb-8">
          <p class="text-red-400 font-bold text-center">⚠️ DOCUMENTO OFICIAL - LEER COMPLETAMENTE ANTES DE PARTICIPAR</p>
        </div>

        <section id="marco-general">
          <h3>1. Marco General y Aceptación</h3>
          <p>La participación en los sorteos organizados por <strong>"Rifas La Favorita"</strong> implica la aceptación total, incondicional e irrevocable de las presentes reglas. El desconocimiento de estas normas <strong>NO exime</strong> de su cumplimiento.</p>
        </section>

        <section id="mecanica">
          <h3>2. Mecánica del Sorteo</h3>
          <div class="bg-gold-500/10 border border-gold-500/30 rounded-xl p-4 my-4">
            <p class="font-bold text-gold-400 mb-2">🎯 Sistema de Sorteo Oficial:</p>
            <p>Nuestros sorteos se rigen estrictamente por los resultados oficiales de la <strong>Lotería del Táchira</strong> en su modalidad <strong>Super Gana</strong>.</p>
          </div>
          <p>El boleto ganador será aquel cuyos dígitos coincidan exactamente con las <strong>cifras finales</strong> del resultado oficial del día programado para el sorteo.</p>
          <p><strong>Ejemplo:</strong> Si el resultado de Super Gana es <code>45-1234</code> y el sorteo es de 4 dígitos, el ticket ganador sería el <code>1234</code>.</p>
        </section>

        <section id="validacion">
          <h3>3. Validación de Tickets</h3>
          <p>Solo se considerarán <strong>VÁLIDOS</strong> aquellos tickets que cumplan TODAS las siguientes condiciones:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>✅ Pago <strong>completamente recibido</strong> y verificado</li>
            <li>✅ Ticket emitido con <strong>código QR y firma criptográfica</strong> única</li>
            <li>✅ Datos del participante <strong>correctos y verificables</strong></li>
            <li>✅ Compra realizada <strong>antes del cierre de ventas</strong> (24h antes del sorteo)</li>
          </ul>
          <p class="text-red-400 mt-4"><strong>⚠️ Tickets sin pago verificado NO participan, sin excepciones.</strong></p>
        </section>

        <section id="premios-reclamo">
          <h3>4. Premios y Reclamo (IMPORTANTE)</h3>
          <div class="bg-red-900/30 border-2 border-red-500 rounded-xl p-6 my-4">
            <h4 class="text-red-400 font-black text-xl mb-3">⏰ PLAZO DE RECLAMO: 7 DÍAS</h4>
            <p class="text-white text-lg">El ganador dispone de un lapso <strong>perentorio e improrrogable</strong> de <strong>SIETE (7) DÍAS CONTINUOS</strong> a partir de la fecha del sorteo para reclamar su premio.</p>
            <p class="text-red-300 mt-3 font-bold">Vencido este plazo, el derecho al premio CADUCA sin excepción alguna.</p>
          </div>
          <p>Para reclamar tu premio necesitas:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>Cédula de identidad original (o pasaporte si es extranjero)</li>
            <li>Ticket digital o impreso con código QR</li>
            <li>Acceso al teléfono/email registrado para verificación</li>
          </ul>
        </section>

        <section id="mayor-comprador">
          <h3>5. Premio al Mayor Comprador (Ranking)</h3>
          <p>En cada sorteo, el participante con <strong>mayor cantidad de tickets comprados</strong> recibe un premio adicional:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>🏆 <strong>Premio actual:</strong> 150 Mautes en Pie</li>
            <li>En caso de empate en cantidad de tickets, se considera el <strong>monto total invertido</strong></li>
            <li>Si persiste el empate, se divide el premio equitativamente</li>
          </ul>
        </section>

        <section id="contingencias">
          <h3>6. Contingencias y Casos Especiales</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li><strong>Si Super Gana no se realiza:</strong> El sorteo se pospone automáticamente a la siguiente fecha oficial</li>
            <li><strong>Si el número ganador no fue vendido:</strong> Se selecciona el número vendido más cercano (hacia arriba)</li>
            <li><strong>Si hay errores técnicos:</strong> Se re-sortea con supervisión de auditor externo</li>
          </ul>
        </section>

        <section id="prohibiciones">
          <h3>7. Prohibiciones y Sanciones</h3>
          <p>Queda estrictamente prohibido:</p>
          <ul class="list-disc pl-6 space-y-2 text-red-300">
            <li>❌ Participar siendo menor de 18 años</li>
            <li>❌ Usar identidades falsas o documentos fraudulentos</li>
            <li>❌ Realizar pagos con fondos ilícitos</li>
            <li>❌ Intentar manipular el sistema de sorteos</li>
            <li>❌ Revender tickets sin autorización</li>
          </ul>
          <p class="mt-4">Las infracciones conllevan <strong>cancelación inmediata de tickets sin reembolso</strong> y denuncia ante autoridades competentes.</p>
        </section>

        <section id="transparencia">
          <h3>8. Transparencia y Auditoría</h3>
          <p>Garantizamos transparencia total:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>📹 Sorteos transmitidos en vivo por Instagram</li>
            <li>📊 Publicación de lista completa de tickets vendidos</li>
            <li>🔐 Verificación de tickets en tiempo real en nuestra plataforma</li>
            <li>📋 Registro auditable de todas las transacciones</li>
          </ul>
        </section>
      </div>
    </article>
  `,
  help: `
    <article itemscope itemtype="https://schema.org/FAQPage">
      <div class="prose prose-invert max-w-none">
        <p class="text-gray-400 mb-8">Encuentra respuestas a las preguntas más frecuentes sobre Rifas La Favorita.</p>

        <div class="space-y-6" role="list">
          <div class="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-gold-500/30 transition-colors" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 class="!mt-0 text-gold-400" itemprop="name">¿Cómo compro un ticket?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <div itemprop="text">
                <ol class="list-decimal pl-6 space-y-2">
                  <li>Haz clic en <strong>"COMPRAR TICKETS"</strong> en la página principal</li>
                  <li>Selecciona la cantidad de tickets que deseas (recomendamos 11 por $200)</li>
                  <li>Llena tus datos personales (nombre, cédula, teléfono, email)</li>
                  <li>Elige tu método de pago preferido</li>
                  <li>Realiza el pago y envía el comprobante por WhatsApp</li>
                  <li>Recibirás tu ticket digital una vez verificado el pago (máx. 24h)</li>
                </ol>
              </div>
            </div>
          </div>

          <div class="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-gold-500/30 transition-colors" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 class="!mt-0 text-gold-400" itemprop="name">¿Cuánto tardan en validar mi pago?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <div itemprop="text">
                <p>Nuestro equipo valida todos los pagos en un máximo de <strong>24 horas hábiles</strong>. Los fines de semana y feriados puede tomar hasta 48 horas.</p>
                <p>Recibirás una notificación por WhatsApp y email cuando tu ticket esté listo.</p>
              </div>
            </div>
          </div>

          <div class="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-gold-500/30 transition-colors" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 class="!mt-0 text-gold-400" itemprop="name">¿Cómo sé si gané?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <div itemprop="text">
                <ul class="list-disc pl-6 space-y-2">
                  <li>📞 <strong>Te llamamos:</strong> Nuestro equipo te contactará inmediatamente</li>
                  <li>📱 <strong>WhatsApp:</strong> Mensaje de confirmación con detalles</li>
                  <li>📧 <strong>Email:</strong> Notificación oficial a tu correo registrado</li>
                  <li>🌐 <strong>Web:</strong> Publicamos los ganadores en nuestra página</li>
                  <li>📸 <strong>Instagram:</strong> Anuncio en vivo y en stories</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-gold-500/30 transition-colors" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 class="!mt-0 text-gold-400" itemprop="name">¿Puedo elegir mis números de ticket?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <div itemprop="text">
                <p><strong>No.</strong> Los números son asignados <strong>aleatoriamente por el sistema</strong> de forma criptográficamente segura para garantizar imparcialidad.</p>
                <p>Esto asegura que todos los participantes tengan exactamente la misma probabilidad de ganar.</p>
              </div>
            </div>
          </div>

          <div class="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-gold-500/30 transition-colors" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 class="!mt-0 text-gold-400" itemprop="name">¿Necesito estar presente en el sorteo?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <div itemprop="text">
                <p><strong>No es necesario.</strong> El sorteo se basa en los resultados oficiales de la Lotería del Táchira (Super Gana), que es un organismo independiente y público.</p>
                <p>Puedes verificar los resultados en cualquier momento en nuestra web o en la página oficial de la lotería.</p>
              </div>
            </div>
          </div>

          <div class="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-gold-500/30 transition-colors" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 class="!mt-0 text-gold-400" itemprop="name">¿Puedo pedir reembolso?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <div itemprop="text">
                <p><strong>Una vez asignados los tickets, las compras son finales</strong> y no se realizan reembolsos.</p>
                <p>Excepciones únicamente en caso de cancelación del sorteo por fuerza mayor, donde se reembolsa el 100% del valor pagado.</p>
              </div>
            </div>
          </div>

          <div class="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-gold-500/30 transition-colors" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
            <h3 class="!mt-0 text-gold-400" itemprop="name">¿Desde qué países puedo participar?</h3>
            <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
              <div itemprop="text">
                <p>🌎 <strong>Cualquier país del mundo.</strong> Aceptamos pagos internacionales via Zelle y Binance.</p>
                <p>Los premios pueden ser entregados en Venezuela o transferidos a cuentas internacionales según el caso.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-gold-500/10 border border-gold-500/30 rounded-xl p-6 mt-8">
          <h3 class="text-gold-400 !mt-0">¿No encontraste tu respuesta?</h3>
          <p>Contáctanos directamente:</p>
          <div class="flex flex-wrap gap-4 mt-4">
            <a href="https://wa.me/584167452305?text=Hola%2C%20tengo%20una%20pregunta" target="_blank" rel="noopener" class="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold px-4 py-2 rounded-lg transition-colors">
              📱 WhatsApp
            </a>
            <a href="mailto:soporte@lafavoritarifas.com" class="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-lg transition-colors">
              ✉️ Email
            </a>
          </div>
        </div>
      </div>
    </article>
  `,
  contact: `
    <article itemscope itemtype="https://schema.org/ContactPage">
      <div class="prose prose-invert max-w-none">
        <p class="text-xl text-gray-300 mb-8">Estamos aquí para ayudarte. Elige el canal que mejor se adapte a tu consulta.</p>

        <div class="grid md:grid-cols-2 gap-6">
          <!-- Atención al Cliente -->
          <div class="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-green-500/30 transition-colors">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-14 h-14 rounded-xl bg-green-600 flex items-center justify-center">
                <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </div>
              <div>
                <h3 class="!mt-0 !mb-1">Atención al Cliente</h3>
                <p class="text-sm text-gray-400 !mb-0">Consultas generales</p>
              </div>
            </div>
            <a href="https://wa.me/584167452305?text=Hola%20Rifas%20La%20Favorita" target="_blank" rel="noopener" class="block text-center bg-green-600 hover:bg-green-500 text-white font-bold py-3 rounded-xl transition-colors">
              +58 416 745 2305
            </a>
          </div>

          <!-- Ventas -->
          <div class="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-green-500/30 transition-colors">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-14 h-14 rounded-xl bg-green-600 flex items-center justify-center">
                <span class="text-2xl">🎫</span>
              </div>
              <div>
                <h3 class="!mt-0 !mb-1">Ventas de Tickets</h3>
                <p class="text-sm text-gray-400 !mb-0">Compra y promociones</p>
              </div>
            </div>
            <a href="https://wa.me/584161356686?text=Hola%2C%20quiero%20comprar%20tickets" target="_blank" rel="noopener" class="block text-center bg-green-600 hover:bg-green-500 text-white font-bold py-3 rounded-xl transition-colors">
              +58 416 135 6686
            </a>
          </div>

          <!-- Soporte Pagos -->
          <div class="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-green-500/30 transition-colors">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-14 h-14 rounded-xl bg-green-600 flex items-center justify-center">
                <span class="text-2xl">💳</span>
              </div>
              <div>
                <h3 class="!mt-0 !mb-1">Soporte de Pagos</h3>
                <p class="text-sm text-gray-400 !mb-0">Verificación y reportes</p>
              </div>
            </div>
            <a href="https://wa.me/584123207976?text=Hola%2C%20necesito%20soporte%20con%20mi%20pago" target="_blank" rel="noopener" class="block text-center bg-green-600 hover:bg-green-500 text-white font-bold py-3 rounded-xl transition-colors">
              +58 412 320 7976
            </a>
          </div>

          <!-- Email -->
          <div class="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-gold-500/30 transition-colors">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-14 h-14 rounded-xl bg-gold-500 flex items-center justify-center">
                <span class="text-2xl">✉️</span>
              </div>
              <div>
                <h3 class="!mt-0 !mb-1">Correo Electrónico</h3>
                <p class="text-sm text-gray-400 !mb-0">Consultas formales</p>
              </div>
            </div>
            <a href="mailto:contacto@lafavoritarifas.com" class="block text-center bg-gold-500 hover:bg-gold-400 text-black font-bold py-3 rounded-xl transition-colors">
              contacto@lafavoritarifas.com
            </a>
          </div>
        </div>

        <!-- Horario -->
        <div class="bg-white/5 rounded-2xl p-6 mt-8 border border-white/10">
          <h3 class="text-gold-400 !mt-0">🕐 Horario de Atención</h3>
          <div class="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <p class="font-bold text-white !mb-1">Lunes a Viernes</p>
              <p class="text-gray-400">8:00 AM - 8:00 PM (Hora Venezuela)</p>
            </div>
            <div>
              <p class="font-bold text-white !mb-1">Sábados y Domingos</p>
              <p class="text-gray-400">9:00 AM - 5:00 PM (Hora Venezuela)</p>
            </div>
          </div>
        </div>

        <!-- Redes Sociales -->
        <div class="mt-8">
          <h3 class="text-gold-400">📱 Síguenos en Redes</h3>
          <div class="flex flex-wrap gap-4 mt-4">
            <a href="https://instagram.com/rifaslafavorita?utm_source=website&utm_medium=contact_page&utm_campaign=social" target="_blank" rel="noopener" class="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity">
              Instagram @rifaslafavorita
            </a>
            <a href="https://facebook.com/rifaslafavorita?utm_source=website&utm_medium=contact_page&utm_campaign=social" target="_blank" rel="noopener" class="inline-flex items-center gap-2 bg-[#1877F2] text-white font-bold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity">
              Facebook
            </a>
            <a href="https://tiktok.com/@rifaslafavorita?utm_source=website&utm_medium=contact_page&utm_campaign=social" target="_blank" rel="noopener" class="inline-flex items-center gap-2 bg-black border border-white/20 text-white font-bold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors">
              TikTok @rifaslafavorita
            </a>
          </div>
        </div>
      </div>
    </article>
  `,
  how_it_works: `
    <article itemscope itemtype="https://schema.org/HowTo">
      <meta itemprop="name" content="Cómo Participar en Rifas La Favorita" />
      <div class="prose prose-invert max-w-none">
        <p class="text-xl text-gray-300 mb-8" itemprop="description">Participar en nuestros sorteos es fácil, rápido y 100% seguro. Sigue estos pasos:</p>

        <!-- Paso 1 -->
        <div class="relative pl-16 pb-12 border-l-2 border-gold-500/30" itemscope itemprop="step" itemtype="https://schema.org/HowToStep">
          <div class="absolute left-0 top-0 -translate-x-1/2 w-12 h-12 rounded-full bg-gold-500 flex items-center justify-center text-black font-black text-xl">1</div>
          <h3 class="text-gold-400 !mt-0" itemprop="name">Selecciona tus Tickets</h3>
          <div itemprop="text">
            <p>Haz clic en el botón <strong>"COMPRAR TICKETS"</strong> y elige cuántos boletos quieres comprar.</p>
            <div class="bg-green-900/30 border border-green-500/30 rounded-xl p-4 my-4">
              <p class="text-green-400 font-bold !mb-2">💡 Recomendación:</p>
              <p class="!mb-0">Compra <strong>11 tickets por $200</strong> y ahorra $20 (1 ticket gratis)</p>
            </div>
            <p>A mayor cantidad de tickets, mayores posibilidades de ganar + entras al ranking de <strong>Mayor Comprador</strong>.</p>
          </div>
        </div>

        <!-- Paso 2 -->
        <div class="relative pl-16 pb-12 border-l-2 border-gold-500/30" itemscope itemprop="step" itemtype="https://schema.org/HowToStep">
          <div class="absolute left-0 top-0 -translate-x-1/2 w-12 h-12 rounded-full bg-gold-500 flex items-center justify-center text-black font-black text-xl">2</div>
          <h3 class="text-gold-400 !mt-0" itemprop="name">Completa tus Datos</h3>
          <div itemprop="text">
            <p>Ingresa tu información personal de forma precisa:</p>
            <ul class="list-disc pl-6 space-y-2">
              <li><strong>Nombre completo</strong> (como aparece en tu cédula)</li>
              <li><strong>Cédula de identidad</strong> (necesaria para reclamar premios)</li>
              <li><strong>Teléfono con WhatsApp</strong> (para notificaciones)</li>
              <li><strong>Correo electrónico</strong> (recibirás tu ticket digital)</li>
            </ul>
          </div>
        </div>

        <!-- Paso 3 -->
        <div class="relative pl-16 pb-12 border-l-2 border-gold-500/30" itemscope itemprop="step" itemtype="https://schema.org/HowToStep">
          <div class="absolute left-0 top-0 -translate-x-1/2 w-12 h-12 rounded-full bg-gold-500 flex items-center justify-center text-black font-black text-xl">3</div>
          <h3 class="text-gold-400 !mt-0" itemprop="name">Realiza el Pago</h3>
          <div itemprop="text">
            <p>Elige tu método de pago preferido:</p>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4 my-4">
              <div class="bg-white/5 rounded-xl p-4 text-center">
                <span class="text-2xl block mb-2">📱</span>
                <p class="text-sm font-bold !mb-0">Pago Móvil</p>
              </div>
              <div class="bg-white/5 rounded-xl p-4 text-center">
                <span class="text-2xl block mb-2">🏦</span>
                <p class="text-sm font-bold !mb-0">Transferencia</p>
              </div>
              <div class="bg-white/5 rounded-xl p-4 text-center">
                <span class="text-2xl block mb-2">💵</span>
                <p class="text-sm font-bold !mb-0">Zelle</p>
              </div>
              <div class="bg-white/5 rounded-xl p-4 text-center">
                <span class="text-2xl block mb-2">🟡</span>
                <p class="text-sm font-bold !mb-0">Binance</p>
              </div>
              <div class="bg-white/5 rounded-xl p-4 text-center">
                <span class="text-2xl block mb-2">💰</span>
                <p class="text-sm font-bold !mb-0">Efectivo</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Paso 4 -->
        <div class="relative pl-16 pb-12 border-l-2 border-gold-500/30" itemscope itemprop="step" itemtype="https://schema.org/HowToStep">
          <div class="absolute left-0 top-0 -translate-x-1/2 w-12 h-12 rounded-full bg-gold-500 flex items-center justify-center text-black font-black text-xl">4</div>
          <h3 class="text-gold-400 !mt-0" itemprop="name">Reporta tu Pago</h3>
          <div itemprop="text">
            <p>Envía el comprobante de pago por WhatsApp al:</p>
            <a href="https://wa.me/584123207976?text=Reporte%20de%20Pago" target="_blank" rel="noopener" class="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold px-6 py-3 rounded-xl my-4">
              📱 +58 412 320 7976
            </a>
            <p>Incluye en tu mensaje:</p>
            <ul class="list-disc pl-6 space-y-2">
              <li>Nombre del cliente</li>
              <li>Número de cédula</li>
              <li>Método de pago</li>
              <li>Referencia</li>
              <li>Captura del comprobante</li>
            </ul>
          </div>
        </div>

        <!-- Paso 5 -->
        <div class="relative pl-16" itemscope itemprop="step" itemtype="https://schema.org/HowToStep">
          <div class="absolute left-0 top-0 -translate-x-1/2 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white font-black text-xl">✓</div>
          <h3 class="text-green-400 !mt-0" itemprop="name">¡Recibe tu Ticket!</h3>
          <div itemprop="text">
            <p>Una vez verificado tu pago (máximo 24 horas), recibirás:</p>
            <ul class="list-disc pl-6 space-y-2">
              <li>🎫 <strong>Ticket digital</strong> con código QR único</li>
              <li>📧 <strong>Confirmación por email</strong></li>
              <li>📱 <strong>Notificación por WhatsApp</strong></li>
            </ul>
            <p class="text-green-400 font-bold mt-4">¡Ya estás participando! Solo espera el día del sorteo.</p>
          </div>
        </div>

        <!-- CTA Final -->
        <div class="bg-gold-500/10 border border-gold-500/30 rounded-2xl p-8 mt-12 text-center">
          <h3 class="text-gold-400 !mt-0 text-2xl">¿Listo para ganar? 🏆</h3>
          <p class="text-lg">No pierdas más tiempo. ¡Tu suerte puede cambiar hoy!</p>
        </div>
      </div>
    </article>
  `,
};

const pageTitle = computed(() => titles[props.page] || 'Información');
const pageContent = computed(() => content[props.page] || '<p>Contenido no disponible</p>');

const handleClose = () => {
  Analytics.pageView('/', 'Inicio');
  emit('close');
};
</script>

<template>
  <div class="max-w-4xl mx-auto p-4 md:p-8 animate-in fade-in slide-in-from-bottom-8">
    <!-- Header -->
    <div class="flex justify-between items-center mb-8">
      <h1 class="text-2xl md:text-4xl font-black text-gold-500">{{ pageTitle }}</h1>
      <button
        @click="handleClose"
        class="text-gray-400 hover:text-white flex items-center gap-2 group"
        aria-label="Volver al inicio"
      >
        <span class="text-xs uppercase font-bold hidden md:inline">Volver</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8 p-1 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Content -->
    <div class="bg-surface border border-white/10 rounded-2xl p-6 md:p-10 shadow-2xl">
      <div v-html="pageContent"></div>
    </div>

    <!-- Back Button -->
    <div class="text-center mt-12">
      <button
        @click="handleClose"
        class="px-10 py-4 bg-white/5 border border-white/10 hover:bg-gold-500 hover:text-black hover:border-gold-500 rounded-full text-white font-bold transition-all duration-300"
      >
        ← Volver al Inicio
      </button>
    </div>
  </div>
</template>

<style scoped>
:deep(h3) {
  font-size: 1.25rem;
  font-weight: bold;
  color: white;
  margin-top: 2rem;
  margin-bottom: 1rem;
}
:deep(h4) {
  font-size: 1.125rem;
  font-weight: bold;
  color: #fbbf24;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}
:deep(p) {
  margin-bottom: 1rem;
  line-height: 1.625;
  color: #d1d5db;
}
:deep(ul) {
  margin-bottom: 1rem;
}
:deep(li) {
  color: #d1d5db;
}
:deep(a) {
  color: #fbbf24;
  transition: color 0.2s;
}
:deep(a:hover) {
  color: #fcd34d;
}
:deep(strong) {
  color: white;
}
:deep(code) {
  background-color: rgba(255, 255, 255, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  color: #fbbf24;
  font-family: monospace;
}
</style>
